import { ThemeName } from "@carfax/web-ui/components/ModulesWithTheme/utils/useTheme";
import { Asset, ContentfulClientApi, Entry, EntrySkeletonType } from "contentful";
import { contentfulClient, cacheTimeoutMilliseconds } from "./contentful-client";
import { ContentType, Image, ModuleContentType, reduceAssetToImage, reduceAssetToVideo } from "./types/content-type";
import { LanguageResolver } from "../i18n/language-resolver";
import { GalleryType } from "@carfax/web-ui/components/Gallery/GalleryType";
import memoryCache from "memory-cache";
import { Log, buildLogEntityWithoutRequest, formatMessage } from "../utils/log";
import {
  PageModular,
  PageModularModuleWrapper,
  PageModularParent,
  ItemQuote,
  ModuleScrollCards,
  ModuleContentNavigation,
  ModuleTip,
  ModuleAnimatedStats,
  ModuleReportOverviewModule,
  ModuleFeatureIntro,
  ModuleDealershipPartnerHeader,
  ModuleIconCards,
  ModuleNumberedSteps,
  ScrollAnchor,
  ModuleAccordionCards,
  ModuleBanner,
  ModuleBolloCalculator,
  ModuleCampaignBanner,
  ModuleCompetitorComparison,
  ModuleDamageExplainer,
  ModuleDataSources,
  ModuleDeviceCarousel,
  ModuleDisclaimer,
  ModuleFeaturesWithImage,
  ModuleFeaturesWithSampleReport,
  ModuleGallery,
  ModuleGeneralVinSearch,
  ModuleHRContactArea,
  ModuleHeroSection,
  ModuleLandingPageVoucherEntry,
  ModulePackageWithoutVin,
  ModulePartnerLogos,
  ModulePictureHeading,
  ModulePricingSection,
  ModuleReportFeatures,
  ModuleSampleVhr,
  ModuleSection,
  ModuleStatCardCarousel,
  ModuleTestimonialModule,
  ModuleTextWithHighlightBoxes,
  ModuleTextWithImage,
  ModuleUsps,
} from "./PageModularModule";
import { ScrollCardColor } from "@carfax/web-ui/components/ScrollCards";
import type { HotSpotType } from "@carfax/web-ui/components/DamageExplainer/components/HotSpotType";
import type { ScrollCardColorType } from "@carfax/web-ui/components/ScrollCards";
import { AccordionStyles } from "domain/CommonModules/AccordionCards";
import { contentfulUtils } from "./contentful.utils";
import {
  TBolloCalculatorFields,
  TItemQuoteFields,
  TLandingPageVoucherEntryFields,
  TModuleAccordionCardsFields,
  TAccordionCard,
  TGenericLabeledImage,
  TModuleBannerFields,
  TModuleCampaignBannerFields,
  TModuleDeviceCarouselFields,
  TModuleDisclaimerFields,
  TModuleGalleryFields,
  TModuleGeneralVinSearchFields,
  TModuleHRContactAreaFields,
  TModulePackageWithoutVinFields,
  TModulePartnerLogosFields,
  TModuleReportFeaturesFields,
  TModuleSampleVhrFields,
  TModuleTextWithImageFields,
  TModuleCompetitorComparisonFields,
  TModuleHeroSectionFields,
  TModuleContentNavigationFields,
  TModuleDamageExplainerFields,
  TModuleTipFields,
  TModuleScrollCardsFields,
  TScrollCardItemFields,
  TModuleStatCardCarouselFields,
  TModuleUspsFields,
  TPageModularFields,
  TPartnerLogo,
  TStatCard,
  TModuleFeatureIntroFields,
  TFeaturesWithImageItemFields,
  TModuleFeaturesWithImageFields,
  TModuleAnimatedStatsFields,
  TModuleTestimonialModuleFields,
  TTestimonialItem,
  TModuleReportOverviewModuleFields,
  TReportOverviewItem,
  TModuleSectionFields,
  TModulePictureHeadingFields,
  TModuleDataSourcesFields,
  TModuleTextWithHighlightBoxesFields,
  THighlightBoxFields,
  TModuleFeaturesWithSampleReportFields,
  TModuleDealershipPartnerHeaderFields,
  TModulePricingSectionFields,
  TPricingSectionCardFields,
  TIconCardFields,
  TModuleIconCardsFields,
  CONTENTFUL_CHAIN_MODIFIER,
  TModuleNumberedStepsFields,
} from "./types/wrappers";
import {
  TypeGenericLabeledImageSkeleton,
  TypeItemQuoteSkeleton,
  TypePageModularSkeleton,
  TypeScrollAnchorSkeleton,
  TypeScrollCardItemSkeleton,
} from "./types/generated";
import StickyVinSearchApi from "./sticky-vin-search-api";

type TParentPageEntry = NonNullable<TPageModularFields["parentPage"]>;

type TContentfulAsset = Asset<CONTENTFUL_CHAIN_MODIFIER>;

type TModuleEntry = Entry<EntrySkeletonType, CONTENTFUL_CHAIN_MODIFIER>;

const isDefined = <T>(value: T | null | undefined): value is T => value !== undefined && value !== null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const omitUndefinedProperties = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const getString = (value: unknown) => (typeof value === "string" ? value : undefined);

const getFiniteNumber = (value: unknown) => {
  const number = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) ? number : undefined;
};

const reduceLinkedImage = (entry: { sys: { id: string } }, asset: TContentfulAsset | undefined) => {
  if (!asset) {
    Log.error(buildLogEntityWithoutRequest(formatMessage("Dropped Contentful entry without image", entry.sys.id)));
    return [];
  }
  return [reduceAssetToImage(asset)];
};

const parseHotSpot = (value: unknown) => {
  const item: Record<string, unknown> = isRecord(value) ? value : {};
  const id = getString(item.id);
  const hotSpotAriaLabel = getString(item.hotSpotAriaLabel);
  const tooltipTitle = getString(item.tooltipTitle);
  const tooltipDescription = getString(item.tooltipDescription);
  const hotSpotDesktopX = getFiniteNumber(item.hotSpotDesktopX);
  const hotSpotDesktopY = getFiniteNumber(item.hotSpotDesktopY);
  const hotSpotMobileX = getFiniteNumber(item.hotSpotMobileX);
  const hotSpotMobileY = getFiniteNumber(item.hotSpotMobileY);

  if (
    id === undefined ||
    hotSpotAriaLabel === undefined ||
    tooltipTitle === undefined ||
    tooltipDescription === undefined ||
    hotSpotDesktopX === undefined ||
    hotSpotDesktopY === undefined ||
    hotSpotMobileX === undefined ||
    hotSpotMobileY === undefined
  ) {
    Log.error(buildLogEntityWithoutRequest(formatMessage("Dropped invalid damage explainer hot spot", id ?? "")));
    return [];
  }

  return [
    {
      id,
      hotSpotDesktopX,
      hotSpotDesktopY,
      hotSpotMobileX,
      hotSpotMobileY,
      hotSpotAriaLabel,
      tooltipTitle,
      tooltipDescription,
    } satisfies HotSpotType,
  ];
};

const hasContentType =
  <S extends EntrySkeletonType>(contentTypeId: S["contentTypeId"]) =>
  (entry: TModuleEntry): entry is Entry<S, CONTENTFUL_CHAIN_MODIFIER> =>
    entry.sys.contentType.sys.id === contentTypeId;

const THEME_MAP: Record<NonNullable<TPageModularFields["theme"]>, ThemeName> = {
  Default: ThemeName.DEFAULT,
  "Bottom Blue with Offset": ThemeName.BOTTOM_BLUE_OFFSET,
};

const requireImage = (asset: TContentfulAsset | undefined) => {
  if (!asset) {
    throw new Error("Missing required Contentful asset");
  }
  return reduceAssetToImage(asset);
};

export default class PageModularApi {
  private static instance: PageModularApi;
  private readonly client: ContentfulClientApi<undefined>;

  private constructor() {
    this.client = contentfulClient.getInstance();
  }

  public static getInstance(): PageModularApi {
    if (PageModularApi.instance) {
      return PageModularApi.instance;
    }

    PageModularApi.instance = new PageModularApi();

    return PageModularApi.instance;
  }

  public async getPageModularBySlug(locale: string, slug: string): Promise<PageModular | null> {
    if (!slug) {
      return null;
    }
    const cacheKey = `pagemodular-${locale}/${slug}`;
    try {
      const cachedPage: PageModular | null = memoryCache.get(cacheKey);
      if (cachedPage) {
        return cachedPage;
      }

      const [entriesResult, stickyVinSearch] = await Promise.all([
        this.client.withoutUnresolvableLinks.getEntries<TypePageModularSkeleton>({
          content_type: ContentType.PageModular,
          locale: locale || LanguageResolver.getCurrentLocale(),
          limit: 1,
          include: 10,
          "fields.slugs": slug,
        }),
        StickyVinSearchApi.getInstance().getModularPageStickyVinSearch(locale, `/${slug}`),
      ]);

      const [entry] = entriesResult.items;
      if (!entry) {
        return null;
      }

      const reducedPage = this.reducePageModular(entry.fields, slug, entry.sys.updatedAt);
      if (stickyVinSearch) {
        reducedPage.stickyVinSearch = stickyVinSearch;
      }
      const page = omitUndefinedProperties(reducedPage);
      memoryCache.put(cacheKey, page, cacheTimeoutMilliseconds);
      return page;
    } catch {
      return null;
    }
  }

  private reducePageModular(fields: TPageModularFields, slug: string, updatedAt: string): PageModular {
    const slugs = Array.isArray(fields.slugs) ? fields.slugs : [];
    return {
      seo: fields.seo?.fields,
      createdAt: fields.seo?.sys.createdAt || null,
      updatedAt,
      lastUpdated: fields.lastUpdated,
      slug,
      slugs,
      modules: this.reducePageModularModules(fields.modules),
      theme: fields.theme ? THEME_MAP[fields.theme] : undefined,
      parentPage: PageModularApi.reduceParentPage(fields.parentPage),
      breadcrumbLabel: fields.breadcrumbLabel || null,
    };
  }

  private static reduceParentPage(
    parentEntry: TParentPageEntry | undefined,
    visited: Set<string> = new Set(),
  ): PageModularParent | undefined {
    if (!parentEntry || visited.has(parentEntry.sys.id)) {
      return undefined;
    }
    visited.add(parentEntry.sys.id);

    const fields = parentEntry.fields;
    const slugs = Array.isArray(fields.slugs) ? fields.slugs : [];

    return {
      title: fields.seo?.fields.title || fields.identifier || "",
      breadcrumbLabel: fields.breadcrumbLabel || null,
      slugs,
      parentPage: PageModularApi.reduceParentPage(fields.parentPage, visited),
    };
  }

  private reducePageModularModules(modules: (TModuleEntry | undefined)[] = []): PageModularModuleWrapper[] {
    const pageModules: PageModularModuleWrapper[] = [];

    modules.filter(isDefined).forEach((module) => {
      try {
        this.addModuleToPage(module, pageModules);
      } catch (error) {
        Log.error(
          buildLogEntityWithoutRequest(
            formatMessage("Skipped Contentful module", module.sys.contentType.sys.id, module.sys.id, String(error)),
          ),
        );
      }
    });

    return pageModules;
  }

  private addModuleToPage(module: TModuleEntry, pageModules: PageModularModuleWrapper[]): void {
    const moduleContentType = module.sys.contentType.sys.id;

    switch (moduleContentType) {
      case ModuleContentType.ModuleSampleVhr: {
        const sampleVhrModule = PageModularApi.reduceSampleVhrModule(
          module.fields as TModuleSampleVhrFields,
          moduleContentType,
        );
        pageModules.push(sampleVhrModule);
        break;
      }

      case ModuleContentType.ModuleReportFeatures: {
        const reportFeatures = PageModularApi.reduceReportFeatures(
          module.fields as TModuleReportFeaturesFields,
          moduleContentType,
        );
        pageModules.push(reportFeatures);
        break;
      }

      case ModuleContentType.ModuleUsps: {
        const usps = PageModularApi.reduceUsps(module.fields as TModuleUspsFields, moduleContentType);
        pageModules.push(usps);
        break;
      }

      case ModuleContentType.ModuleBanner: {
        const banner = PageModularApi.reduceBannerModule(module.fields as TModuleBannerFields, moduleContentType);
        pageModules.push(banner);
        break;
      }

      case ModuleContentType.ModuleTextWithImage: {
        const textWithImage = PageModularApi.reduceTextAndImage(
          module.fields as TModuleTextWithImageFields,
          moduleContentType,
        );
        pageModules.push(textWithImage);
        break;
      }

      case ModuleContentType.ModulePartnerLogos: {
        const partnerLogos = PageModularApi.reducePartnerLogos(
          module.fields as TModulePartnerLogosFields,
          moduleContentType,
        );
        pageModules.push(partnerLogos);
        break;
      }

      case ModuleContentType.ModuleGallery: {
        const gallery = PageModularApi.reduceGallery(module.fields as TModuleGalleryFields, moduleContentType);
        pageModules.push(gallery);
        break;
      }

      case ModuleContentType.ModuleHRContactArea: {
        const hrArea = PageModularApi.reduceHrArea(module.fields as TModuleHRContactAreaFields, moduleContentType);
        pageModules.push(hrArea);
        break;
      }
      case ModuleContentType.ModuleAccordionCards: {
        const accordionCards = PageModularApi.reduceAccordionCards(
          module.fields as TModuleAccordionCardsFields,
          moduleContentType,
        );
        pageModules.push(accordionCards);
        break;
      }

      case ModuleContentType.ModuleDeviceCarousel: {
        const deviceCarousel = PageModularApi.reduceDeviceCarousel(
          module.fields as TModuleDeviceCarouselFields,
          moduleContentType,
        );
        pageModules.push(deviceCarousel);
        break;
      }

      case ModuleContentType.ModuleDisclaimer: {
        const disclaimerModule = PageModularApi.reduceDisclaimerModule(
          module.fields as TModuleDisclaimerFields,
          moduleContentType,
        );
        pageModules.push(disclaimerModule);
        break;
      }

      case ModuleContentType.ModuleCampaignBanner: {
        const campaignBanner = PageModularApi.reduceCampaignBannerModule(
          module.fields as TModuleCampaignBannerFields,
          moduleContentType,
        );
        pageModules.push(campaignBanner);
        break;
      }

      case ModuleContentType.ModulePackageWithoutVin: {
        const packageWithoutVin = PageModularApi.reducePackageWithoutVin(
          module.fields as TModulePackageWithoutVinFields,
          moduleContentType,
        );
        pageModules.push(packageWithoutVin);
        break;
      }

      case ModuleContentType.ModuleGeneralVinSearch: {
        const generalVinSearch = PageModularApi.reduceGeneralVinSearch(
          module.fields as TModuleGeneralVinSearchFields,
          moduleContentType,
        );
        pageModules.push(generalVinSearch);
        break;
      }

      case ModuleContentType.ModuleLandingPageVoucherEntry: {
        const landingPageVoucherEntry = PageModularApi.reduceLandingPageVoucherEntryModule(
          module.fields as TLandingPageVoucherEntryFields,
          moduleContentType,
        );
        pageModules.push(landingPageVoucherEntry);
        break;
      }

      case ModuleContentType.ModuleBolloCalculator: {
        const bolloCalculator = PageModularApi.reduceBolloComponent(
          module.fields as TBolloCalculatorFields,
          moduleContentType,
        );
        pageModules.push(bolloCalculator);
        break;
      }
      case ModuleContentType.ModuleCompetitorComparison: {
        const competitorComparison = PageModularApi.reduceCompetitorComparison(
          module.fields as TModuleCompetitorComparisonFields,
          moduleContentType,
        );
        pageModules.push(competitorComparison);
        break;
      }

      case ModuleContentType.ModuleDamageExplainer: {
        const damageExplainer = PageModularApi.reduceDamageExplainer(
          module.fields as TModuleDamageExplainerFields,
          moduleContentType,
        );
        pageModules.push(damageExplainer);
        break;
      }

      case ModuleContentType.ModuleScrollCards: {
        const scrollCards = PageModularApi.reduceScrollCards(
          module.fields as TModuleScrollCardsFields,
          moduleContentType,
        );
        pageModules.push(scrollCards);
        break;
      }
      case ModuleContentType.ModuleHeroSection: {
        const heroSection = PageModularApi.reduceHeroSection(
          module.fields as TModuleHeroSectionFields,
          moduleContentType,
        );
        pageModules.push(heroSection);
        break;
      }
      case ModuleContentType.ModuleStatCardCarousel: {
        const statCardCarousel = PageModularApi.reduceStatCardCarousel(
          module.fields as TModuleStatCardCarouselFields,
          moduleContentType,
        );
        pageModules.push(statCardCarousel);
        break;
      }

      case ModuleContentType.ModuleFeatureIntro: {
        const featureIntro = PageModularApi.reduceFeatureIntro(
          module.fields as TModuleFeatureIntroFields,
          moduleContentType,
        );
        pageModules.push(featureIntro);
        break;
      }

      case ModuleContentType.ModuleFeaturesWithImage: {
        const featuresWithImage = PageModularApi.reduceFeaturesWithImage(
          module.fields as TModuleFeaturesWithImageFields,
          moduleContentType,
        );
        pageModules.push(featuresWithImage);
        break;
      }

      case ModuleContentType.ModulePictureHeading: {
        const pictureHeading = PageModularApi.reducePictureHeading(
          module.fields as TModulePictureHeadingFields,
          moduleContentType,
        );
        pageModules.push(pictureHeading);
        break;
      }

      case ModuleContentType.ModuleAnimatedStats: {
        const animatedStats = PageModularApi.reduceAnimatedStats(
          module.fields as TModuleAnimatedStatsFields,
          moduleContentType,
        );
        pageModules.push(animatedStats);
        break;
      }

      case ModuleContentType.ModuleContentNavigation: {
        const contentNavigation = PageModularApi.reduceContentNavigation(
          module.fields as TModuleContentNavigationFields,
          moduleContentType,
        );
        pageModules.push(contentNavigation);
        break;
      }

      case ModuleContentType.ModuleTip: {
        const tip = PageModularApi.reduceTip(module.fields as TModuleTipFields, moduleContentType);
        pageModules.push(tip);
        break;
      }

      case ModuleContentType.ModuleTestimonialModule: {
        const testimonialModule = PageModularApi.reduceTestimonialModule(
          module.fields as TModuleTestimonialModuleFields,
          moduleContentType,
        );
        if (testimonialModule) pageModules.push(testimonialModule);
        break;
      }

      case ModuleContentType.ModuleReportOverviewModule: {
        const reportOverviewModule = PageModularApi.reduceReportOverviewModule(
          module.fields as TModuleReportOverviewModuleFields,
          moduleContentType,
        );
        if (reportOverviewModule) pageModules.push(reportOverviewModule);
        break;
      }

      case ModuleContentType.ModuleDealershipPartnerHeader: {
        const dealershipPartnerHeader = PageModularApi.reduceDealershipPartnerHeader(
          module.fields as TModuleDealershipPartnerHeaderFields,
          moduleContentType,
        );
        pageModules.push(dealershipPartnerHeader);
        break;
      }

      case ModuleContentType.ModuleSection: {
        const sectionFields = module.fields as TModuleSectionFields;
        const nestedModules = this.reducePageModularModules(sectionFields.modules || []);
        pageModules.push({
          contentType: moduleContentType,
          module: {
            identifier: sectionFields.identifier || "",
            modules: nestedModules,
            paddingTop: sectionFields.paddingTop || "medium",
            paddingBottom: sectionFields.paddingBottom || "medium",
            innerSpacing: sectionFields.innerSpacing || "none",
          } satisfies ModuleSection,
        });
        break;
      }

      case ModuleContentType.ModuleDataSources: {
        const dataSources = PageModularApi.reduceDataSourcesModule(
          module.fields as TModuleDataSourcesFields,
          moduleContentType,
        );

        pageModules.push(dataSources);
        break;
      }

      case ModuleContentType.ModuleTextWithHighlightBoxes: {
        const textWithHighlightBoxes = PageModularApi.reduceTextWithHighlightBoxes(
          module.fields as TModuleTextWithHighlightBoxesFields,
          moduleContentType,
        );
        pageModules.push(textWithHighlightBoxes);
        break;
      }

      case ModuleContentType.ModuleFeaturesWithSampleReport: {
        const featuresWithSampleReport = PageModularApi.reduceFeaturesWithSampleReport(
          module.fields as TModuleFeaturesWithSampleReportFields,
          moduleContentType,
        );
        pageModules.push(featuresWithSampleReport);
        break;
      }

      case ModuleContentType.ModulePricingSection: {
        const pricingSection = PageModularApi.reducePricingSection(
          module.fields as TModulePricingSectionFields,
          moduleContentType,
        );
        pageModules.push(pricingSection);
        break;
      }

      case ModuleContentType.ModuleIconCards: {
        const iconCards = PageModularApi.reduceIconCards(module.fields as TModuleIconCardsFields, moduleContentType);
        pageModules.push(iconCards);
        break;
      }

      case ModuleContentType.ModuleNumberedSteps: {
        const numberedSteps = PageModularApi.reduceNumberedSteps(
          module.fields as TModuleNumberedStepsFields,
          moduleContentType,
        );
        pageModules.push(numberedSteps);
        break;
      }

      default:
    }
  }

  private static reduceHrArea(
    fields: TModuleHRContactAreaFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const image = requireImage(fields.photo);
    return {
      contentType: moduleContentType,
      module: {
        headline: fields.headline,
        photo: image.url,
        photoAlt: image.title,
        name: fields.name,
        position: fields.position,
        email: fields.email,
      } satisfies ModuleHRContactArea,
    };
  }

  private static reduceSampleVhrModule(
    fields: TModuleSampleVhrFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        primaryDesktopImage: requireImage(fields.primaryDesktopImage),
        secondaryDesktopImage: requireImage(fields.secondaryDesktopImage),
        primaryMobileImage: requireImage(fields.primaryMobileImage),
        secondaryMobileImage: requireImage(fields.secondaryMobileImage),
        text: fields.text,
        optionalDesktopReport: fields.optionalDesktopReport
          ? reduceAssetToImage(fields.optionalDesktopReport)
          : undefined,
        optionalMobileReport: fields.optionalMobileReport ? reduceAssetToImage(fields.optionalMobileReport) : undefined,
      } satisfies ModuleSampleVhr,
    };
  }

  private static reduceBannerModule(
    fields: TModuleBannerFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        description: fields.description,
        dateToEndCampaign: fields.dateToEndCampaign,
        optionalImage: fields.optionalImage ? reduceAssetToImage(fields.optionalImage) : undefined,
        themePattern: fields.themePattern || "",
        daysLabel: fields.days,
        hoursLabel: fields.hours,
        minutesLabel: fields.minutes,
        secondsLabel: fields.seconds,
      } satisfies ModuleBanner,
    };
  }

  private static reduceDisclaimerModule(
    fields: TModuleDisclaimerFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        textContent: fields.textContent,
      } satisfies ModuleDisclaimer,
    };
  }

  private static reducePartnerLogos(
    fields: TModulePartnerLogosFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const reducePartnerLogo = (partnerLogo: TPartnerLogo) =>
      reduceLinkedImage(partnerLogo, partnerLogo.fields.partnerLogo).map((image) => ({
        partnerSiteUrl: partnerLogo.fields.partnerSiteUrl,
        partnerLogo: image,
        ariaLabel: partnerLogo.fields.ariaLabel ?? "",
      }));
    return {
      contentType: moduleContentType,
      module: {
        title: fields.headline,
        partnerLogos: fields.partnerLogos.filter(isDefined).flatMap(reducePartnerLogo),
      } satisfies ModulePartnerLogos,
    };
  }

  private static reduceReportFeatures(
    fields: TModuleReportFeaturesFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const reduceListElement = (element: unknown) => {
      const item: Record<string, unknown> = isRecord(element) ? element : {};
      const name = getString(item.name);
      return name === undefined ? [] : [{ name, tag: getString(item.tag), url: getString(item.url) }];
    };

    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        seeMore: fields.seeMore,
        seeLess: fields.seeLess,
        list: Array.isArray(fields.list) ? fields.list.flatMap(reduceListElement) : [],
      } satisfies ModuleReportFeatures,
    };
  }

  private static reduceUsps(fields: TModuleUspsFields, moduleContentType: ModuleContentType): PageModularModuleWrapper {
    const reduceItems = (item: TGenericLabeledImage) =>
      reduceLinkedImage(item, item.fields.image).map((image) => ({
        label: item.fields.label,
        image,
        text: item.fields.text,
      }));
    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        subtitle: fields.subtitle,
        type: fields.type,
        items: fields.items ? fields.items.filter(isDefined).flatMap(reduceItems) : [],
      } satisfies ModuleUsps,
    };
  }

  private static reduceTextAndImage(
    fields: TModuleTextWithImageFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        text: fields.text,
        videoUrl: fields.video ? fields.video : undefined,
        videoTitle: fields.videoTitle,
        image: fields.image ? reduceAssetToImage(fields.image) : undefined,
        ctaLabel: fields.ctaButton?.fields.label,
        ctaLink: fields.ctaButton?.fields.link,
        inverted: fields.inverted || false,
        textAlign: fields.textAlign,
        contentfulVideo: fields.contentfulVideo ? reduceAssetToVideo(fields.contentfulVideo) : undefined,
        isAnimation: fields.isAnimation || false,
        optionalTitle: fields.optionalTitle || "",
        optionalDesktopReport: fields.optionalDesktopReport
          ? reduceAssetToImage(fields.optionalDesktopReport)
          : undefined,
        optionalMobileReport: fields.optionalMobileReport ? reduceAssetToImage(fields.optionalMobileReport) : undefined,
        mobileImage: fields.mobileImage ? reduceAssetToImage(fields.mobileImage) : undefined,
        sampleReportButtonText: fields.sampleReportButtonText || "",
        isLoop: fields.isLoop || false,
      } satisfies ModuleTextWithImage,
    };
  }

  private static reduceAccordionCards(
    fields: TModuleAccordionCardsFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const reduceItems = (item: TAccordionCard) => ({
      title: item.fields.title,
      image: item.fields.image ? reduceAssetToImage(item.fields.image) : undefined,
      visibleText: item.fields.visibleText,
      hiddenText: item.fields.hiddenText,
      seeMoreText: item.fields.seeMoreText ?? "",
      seeLessText: item.fields.seeLessText ?? "",
    });
    return {
      contentType: moduleContentType,
      module: {
        header: fields.header,
        identifier: fields.identifier ? contentfulUtils.slugify(fields.identifier) : "",
        accordionCards: fields.accordionCard ? fields.accordionCard.filter(isDefined).map(reduceItems) : [],
        accordionStyle: fields.accordionStyle ? AccordionStyles[fields.accordionStyle] : AccordionStyles.SeeMore,
        expanded: fields.expanded ? fields.expanded : false,
      } satisfies ModuleAccordionCards,
    };
  }

  private static reduceGallery(
    fields: TModuleGalleryFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        type: GalleryType[fields.type] || GalleryType.Photo,
        images: PageModularApi.reduceGalleryItems(fields),
      } satisfies ModuleGallery,
    };
  }

  private static reduceGalleryItems(fields: TModuleGalleryFields): (ItemQuote | Image)[] {
    const items = (fields.images ?? []).filter(isDefined);

    if (GalleryType.Quote === fields.type) {
      return items
        .filter(hasContentType<TypeItemQuoteSkeleton>("itemQuote"))
        .map(({ fields: quote }) => PageModularApi.reduceQuote(quote));
    }

    return items
      .filter(hasContentType<TypeGenericLabeledImageSkeleton>("genericLabeledImage"))
      .flatMap((labeledImage) => reduceLinkedImage(labeledImage, labeledImage.fields.image));
  }

  private static reduceQuote(fields: TItemQuoteFields): ItemQuote {
    return {
      quote: fields.quote,
      name: fields.name || "",
      jobTitle: fields.jobTitle || "",
    };
  }

  private static reduceGeneralVinSearch(
    fields: TModuleGeneralVinSearchFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        header: fields.header,
        headerWithoutVin: fields.headerWithoutVin || "",
        bottomSubtitle: fields.bottomSubtitle || "",
        placeholder: fields.placeholder || "",
        subtitle: fields.subtitle || "",
        subtitleWithoutVin: fields.subtitleWithoutVin || "",
        componentType: fields.componentType,
        partnerLogo: fields.partnerLogo ? reduceAssetToImage(fields.partnerLogo) : undefined,
        bottomSubtitleUrl: fields.bottomSubtitleUrl || "",
        isZoomEnabled: fields.isZoomEnabled || false,
        isLicensePlateDetection: fields.isLicensePlateDetection || false,
      } satisfies ModuleGeneralVinSearch,
    };
  }

  private static reduceDeviceCarousel(
    fields: TModuleDeviceCarouselFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        titleRichText: fields.titleRichText,
        desktopImages: fields.desktopImages.filter(isDefined).map(reduceAssetToImage),
        mobileImages: fields.mobileImages.filter(isDefined).map(reduceAssetToImage),
        buttonText: fields.buttonText,
        optionalDesktopReport: fields.optionalDesktopReport
          ? reduceAssetToImage(fields.optionalDesktopReport)
          : undefined,
        optionalMobileReport: fields.optionalMobileReport ? reduceAssetToImage(fields.optionalMobileReport) : undefined,
      } satisfies ModuleDeviceCarousel,
    };
  }

  private static getStringOrEmpty(value: string | undefined): string {
    return value || "";
  }

  private static reduceOptionalAsset(asset: TContentfulAsset | undefined) {
    return asset ? reduceAssetToImage(asset) : undefined;
  }

  private static reduceTrustIconsList(trustIcons: (TContentfulAsset | undefined)[] | undefined): Image[] {
    return Array.isArray(trustIcons) ? trustIcons.filter(isDefined).map(reduceAssetToImage) : [];
  }

  private static reduceCampaignBannerModule(
    fields: TModuleCampaignBannerFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        description: fields.description,
        image: PageModularApi.reduceOptionalAsset(fields.image),
        imageSize: PageModularApi.getStringOrEmpty(fields.imageSize),
        ctaButtonLabel: PageModularApi.getStringOrEmpty(fields.ctaButtonLabel),
        ctaButtonUrl: PageModularApi.getStringOrEmpty(fields.ctaButtonUrl),
        partnerLogo: PageModularApi.reduceOptionalAsset(fields.partnerLogo),
        partnerNoteText: PageModularApi.getStringOrEmpty(fields.partnerNoteText),
        anchorLinkLabel: PageModularApi.getStringOrEmpty(fields.anchorLinkLabel),
        displayVinSearch: fields.displayVinSearch || false,
        modalText: PageModularApi.getStringOrEmpty(fields.modalText),
        customPlaceholder: PageModularApi.getStringOrEmpty(fields.customPlaceholder),
        withSearchButtonText: PageModularApi.getStringOrEmpty(fields.searchButtonText),
        trustIconsText: PageModularApi.getStringOrEmpty(fields.trustIconsText),
        trustIcons: PageModularApi.reduceTrustIconsList(fields.trustIcons),
        previewPageSlug: PageModularApi.getStringOrEmpty(fields.previewPageUrl),
        sampleReportText: PageModularApi.getStringOrEmpty(fields.sampleReportText),
        bulletPoints: fields.bulletPoints,
        vinSearchPrimaryText: fields.vinSearchPrimaryText || null,
        vinSearchSecondaryText: fields.vinSearchSecondaryText || null,
      } satisfies ModuleCampaignBanner,
    };
  }

  private static reducePackageWithoutVin(
    fields: TModulePackageWithoutVinFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        subtitle: fields.subtitle,
        voucherText: fields.voucherText,
        voucher: fields.voucher,
      } satisfies ModulePackageWithoutVin,
    };
  }

  private static reduceLandingPageVoucherEntryModule(
    fields: TLandingPageVoucherEntryFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: module,
      module: {
        headline: fields.headline,
        description: fields.description,
        ctaText: fields.ctaText,
        ctaUrl: fields.ctaUrl,
        picture: PageModularApi.reduceOptionalAsset(fields.picture),
        packageType: fields.packageType,
      } satisfies ModuleLandingPageVoucherEntry,
    };
  }

  private static reduceCompetitorComparison(
    fields: TModuleCompetitorComparisonFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: module,
      module: {
        headline: fields.headline || "",
        text: fields.text || "",
      } satisfies ModuleCompetitorComparison,
    };
  }

  private static reduceDamageExplainer(
    fields: TModuleDamageExplainerFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: module,
      module: {
        identifier: fields.identifier || "",
        backgroundColor: fields.backgroundColor || "",
        backgroundDesktop: requireImage(fields.backgroundDesktop),
        backgroundMobile: requireImage(fields.backgroundMobile),
        hotSpots: Array.isArray(fields.hotSpots) ? fields.hotSpots.flatMap(parseHotSpot) : [],
      } satisfies ModuleDamageExplainer,
    };
  }

  private static reduceScrollCards(
    fields: TModuleScrollCardsFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    const COLOR_MAP: Record<TScrollCardItemFields["color"], ScrollCardColorType> = {
      YellowGreen: ScrollCardColor.YellowGreen,
      LightGreen: ScrollCardColor.LightGreen,
      BabyBlue: ScrollCardColor.BabyBlue,
      Gray: ScrollCardColor.Gray,
    };

    const cards = fields.cards
      .filter(isDefined)
      .filter(hasContentType<TypeScrollCardItemSkeleton>("scrollCardItem"))
      .map(({ fields: card }) => ({
        heading: card.heading,
        text: card.text,
        color: COLOR_MAP[card.color],
      }));

    return {
      contentType: module,
      module: {
        identifier: fields.identifier ? contentfulUtils.slugify(fields.identifier) : undefined,
        cards,
      } satisfies ModuleScrollCards,
    };
  }

  private static reduceHeroSection(
    fields: TModuleHeroSectionFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: module,
      module: {
        heading: fields.heading || "",
        heroImageUrl: fields.heroImage ? reduceAssetToImage(fields.heroImage).url : undefined,
        heroImageAlt: fields.heroImageAlt || "",
        text: fields.text,
        additionalText: fields.additionalText,
        keyPointsHeader: fields.keyPointsHeader || "",
        keyPoints: fields.keyPoints || [],
        keyPointImageUrl: fields.keyPointsImage ? reduceAssetToImage(fields.keyPointsImage).url : undefined,
        keyPointImageAlt: fields.keyPointsImageAlt || "",
      } satisfies ModuleHeroSection,
    };
  }

  private static reduceBolloComponent(
    fields: TBolloCalculatorFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: module,
      module: {
        heading: fields.heading,
        subtitle: fields.subtitle,
        information: fields.information,
        isHeadingH2: fields.isHeadingH2 || false,
        hideVinSearchBollo: fields.hideVinSearchBollo || false,
      } satisfies ModuleBolloCalculator,
    };
  }

  private static reduceStatCardCarousel(
    fields: TModuleStatCardCarouselFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const reduceCard = (card: TStatCard) =>
      reduceLinkedImage(card, card.fields.icon).map((icon) => ({
        icon,
        statValue: card.fields.statValue,
        subtitle: card.fields.subtitle,
        description: card.fields.description,
      }));

    return {
      contentType: moduleContentType,
      module: {
        heading: fields.heading,
        cards: fields.cards ? fields.cards.filter(isDefined).flatMap(reduceCard) : [],
      } satisfies ModuleStatCardCarousel,
    };
  }

  private static reduceFeatureIntro(
    fields: TModuleFeatureIntroFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    const BACKGROUND_COLOR_MAP: Record<string, ModuleFeatureIntro["backgroundColor"]> = {
      White: "white",
      "Mist Tint": "mistTint",
      Transparent: "transparent",
    };

    return {
      contentType: module,
      module: {
        headlineVariant: fields.headlineVariant,
        headlineText: fields.headlineText,
        badgeText: fields.badgeText,
        description: fields.description,
        buttonText: fields.buttonText,
        backgroundColor: BACKGROUND_COLOR_MAP[fields.backgroundColor],
      } satisfies ModuleFeatureIntro,
    };
  }

  private static reduceContentNavigation(
    fields: TModuleContentNavigationFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const items = fields.items.filter(isDefined).map((entry) => {
      const fields = entry.fields;
      return {
        label: fields.label,
        url: fields.url,
        icon: fields.icon ? reduceAssetToImage(fields.icon) : undefined,
      };
    });

    return {
      contentType: moduleContentType,
      module: {
        badge: fields.badge || "",
        headline: fields.headline || "",
        headingLevel: fields.headingLevel,
        items,
      } satisfies ModuleContentNavigation,
    };
  }

  private static reduceTip(fields: TModuleTipFields, moduleContentType: ModuleContentType): PageModularModuleWrapper {
    const BACKGROUND_COLOR_MAP: Record<string, ModuleTip["backgroundColor"]> = {
      White: "white",
      "Mist Tint": "mistTint",
      Transparent: "transparent",
    };

    return {
      contentType: moduleContentType,
      module: {
        identifier: fields.identifier || "",
        backgroundColor: BACKGROUND_COLOR_MAP[fields.backgroundColor] || undefined,
        title: fields.title || "",
        headingLevel: fields.headingLevel || undefined,
        description: fields.description || null,
      } satisfies ModuleTip,
    };
  }

  private static reduceFeaturesWithImage(
    fields: TModuleFeaturesWithImageFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    const reduceFeature = (entry: { fields: TFeaturesWithImageItemFields }) => ({
      title: entry.fields.title,
      description: entry.fields.description,
      icon: entry.fields.icon ? reduceAssetToImage(entry.fields.icon) : undefined,
    });

    return {
      contentType: module,
      module: {
        badgeText: fields.badgeText,
        headline: fields.headline,
        headingLevel: fields.headingLevel,
        features: Array.isArray(fields.features) ? fields.features.filter(isDefined).map(reduceFeature) : [],
        buttonLabelExpanded: fields.buttonLabelExpanded,
        buttonLabelCollapsed: fields.buttonLabelCollapsed,
        mobileIllustration: requireImage(fields.mobileIllustration),
        desktopIllustration: requireImage(fields.desktopIllustration),
        defaultExpanded: fields.defaultExpanded || false,
      } satisfies ModuleFeaturesWithImage,
    };
  }

  private static reduceFeaturesWithSampleReport(
    fields: TModuleFeaturesWithSampleReportFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    const reduceFeature = (entry: { fields: TFeaturesWithImageItemFields }) => ({
      title: entry.fields.title,
      text: entry.fields.description,
      icon: entry.fields.icon ? reduceAssetToImage(entry.fields.icon) : undefined,
    });

    return {
      contentType: module,
      module: {
        headline: fields.headline,
        features: Array.isArray(fields.features) ? fields.features.filter(isDefined).map(reduceFeature) : [],
        illustration: requireImage(fields.illustration),
        sampleVhrButtonLabel: fields.sampleVhrButtonLabel,
        sampleVhrMobileImage: requireImage(fields.sampleVhrMobileImage),
        sampleVhrDesktopImage: requireImage(fields.sampleVhrDesktopImage),
      } satisfies ModuleFeaturesWithSampleReport,
    };
  }

  private static reducePictureHeading(
    fields: TModulePictureHeadingFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    const illustration = fields.ilustration ? reduceAssetToImage(fields.ilustration) : null;
    return {
      contentType: module,
      module: {
        heading: fields.heading || "",
        description: fields.description || null,
        illustrationUrl: illustration?.url || null,
        illustrationAlt: illustration?.title || null,
        headingLevel: fields.headingLevel || "h2",
        tag: fields.labelTag || null,
      } satisfies ModulePictureHeading,
    };
  }

  private static reduceAnimatedStats(
    fields: TModuleAnimatedStatsFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    const image = fields.image ? reduceAssetToImage(fields.image) : null;
    return {
      contentType: module,
      module: {
        tagline: fields.tagline,
        stat1Value: fields.stat1Value,
        stat1Suffix: fields.stat1Suffix,
        stat1Label: fields.stat1Label,
        stat2Value: fields.stat2Value,
        stat2Suffix: fields.stat2Suffix,
        stat2Label: fields.stat2Label,
        stat3Value: fields.stat3Value,
        stat3Suffix: fields.stat3Suffix,
        stat3Label: fields.stat3Label,
        imageUrl: image ? image.url : undefined,
        imageAlt: fields.imageAlt || "",
      } satisfies ModuleAnimatedStats,
    };
  }

  private static reduceTestimonialModule(
    fields: TModuleTestimonialModuleFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper | null {
    const reduceTestimonial = (entry: TTestimonialItem) => ({
      text: entry.fields.text,
      customerName: entry.fields.customerName,
      location: entry.fields.location,
    });

    const testimonials = Array.isArray(fields.testimonials)
      ? fields.testimonials.filter(isDefined).map(reduceTestimonial)
      : [];

    if (testimonials.length === 0) return null;

    const [first, ...rest] = testimonials;

    const trustStatPairs = [
      { value: fields.averageRatingValue, label: fields.averageRatingLabel },
      { value: fields.reportsIssuedValue, label: fields.reportsIssuedLabel },
      { value: fields.countriesCoveredValue, label: fields.countriesCoveredLabel },
    ];
    const trustStats = trustStatPairs.flatMap(({ value, label }) => (value && label ? [{ value, label }] : []));

    return {
      contentType: moduleContentType,
      module: {
        heading: fields.heading,
        headingLevel: fields.headingLevel,
        testimonials: [first, ...rest],
        trustStats,
      } satisfies ModuleTestimonialModule,
    };
  }

  private static reduceScrollAnchor(
    entry?: Entry<TypeScrollAnchorSkeleton, "WITHOUT_UNRESOLVABLE_LINKS">,
  ): ScrollAnchor | undefined {
    if (!entry) return undefined;
    const { fields } = entry;
    const ctaIconImage = fields.ctaIcon ? reduceAssetToImage(fields.ctaIcon) : null;
    return {
      anchorId: PageModularApi.getStringOrEmpty(fields.anchorId),
      ctaLabel: PageModularApi.getStringOrEmpty(fields.ctaLabel),
      scrollFocus: fields.scrollFocus ?? false,
      ctaIcon: ctaIconImage ? ctaIconImage.url : null,
    };
  }

  private static reduceDealershipPartnerHeader(
    fields: TModuleDealershipPartnerHeaderFields,
    module: ModuleContentType,
  ): PageModularModuleWrapper {
    const partnerLogo = fields.partnerLogo ? reduceAssetToImage(fields.partnerLogo) : null;
    const carPlaceholderImage = fields.carPlaceholderImage ? reduceAssetToImage(fields.carPlaceholderImage) : null;
    return {
      contentType: module,
      module: {
        title: fields.title || "",
        subtitle: fields.subtitle || "",
        ctaNewUser: fields.ctaNewUser || "",
        ctaNoBudget: fields.ctaNoBudget || "",
        partnerLogoUrl: partnerLogo ? partnerLogo.url : undefined,
        partnerLogoAlt: partnerLogo ? partnerLogo.title : undefined,
        officialPartnerLabel: fields.officialPartnerLabel || undefined,
        statusBadgeLabel: fields.statusBadgeLabel || undefined,
        cardTitle: fields.cardTitle || undefined,
        trustBadgeText: fields.trustBadgeText || undefined,
        packagesSectionTitle: fields.packagesSectionTitle || undefined,
        creditsUnlockTitle: fields.creditsUnlockTitle || undefined,
        creditsLabel: fields.creditsLabel || undefined,
        carPlaceholderImageUrl: carPlaceholderImage ? carPlaceholderImage.url : undefined,
        scrollAnchor: PageModularApi.reduceScrollAnchor(fields.scrollAnchor),
      } satisfies ModuleDealershipPartnerHeader,
    };
  }

  private static reduceReportOverviewModule(
    fields: TModuleReportOverviewModuleFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper | null {
    const BACKGROUND_MAP: Record<string, ModuleReportOverviewModule["background"]> = {
      White: "white",
      "Mist Tint": "mistTint",
    };

    const reduceSection = (entry: TReportOverviewItem) =>
      reduceLinkedImage(entry, entry.fields.image).map((image) => ({
        heading: entry.fields.heading,
        description: entry.fields.description,
        image,
        imageAlt: entry.fields.imageAlt,
      }));

    const sections = Array.isArray(fields.sections) ? fields.sections.filter(isDefined).flatMap(reduceSection) : [];

    if (sections.length < 2) return null;

    const [first, second, ...rest] = sections;

    return {
      contentType: moduleContentType,
      module: {
        badgeText: fields.badgeText || undefined,
        background: BACKGROUND_MAP[fields.backgroundColor] ?? "white",
        headingLevel: fields.headingLevel,
        sections: [first, second, ...rest],
        sampleReportHeading: fields.sampleReportHeading,
        sampleReportLabel: fields.sampleReportLabel,
        sampleReportIcon: requireImage(fields.sampleReportIcon),
      } satisfies ModuleReportOverviewModule,
    };
  }

  private static reduceDataSourcesModule(
    fields: TModuleDataSourcesFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        description: fields.description,
        items: fields.items,
        itemsNote: fields.itemsNote,
      } satisfies ModuleDataSources,
    };
  }

  private static reduceTextWithHighlightBoxes(
    fields: TModuleTextWithHighlightBoxesFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const items = Array.isArray(fields.highlights)
      ? fields.highlights.filter(isDefined).map((entry: { fields: THighlightBoxFields }) => ({
          heading: entry.fields.heading,
          text: entry.fields.text,
        }))
      : [];

    return {
      contentType: moduleContentType,
      module: {
        badge: fields.badge || undefined,
        heading: fields.heading || "",
        text: fields.text || undefined,
        items,
        disclaimer: fields.disclaimer || undefined,
      } satisfies ModuleTextWithHighlightBoxes,
    };
  }

  private static reducePricingSection(
    fields: TModulePricingSectionFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const pricingCards = Array.isArray(fields.pricingCards)
      ? fields.pricingCards.filter(isDefined).map((entry: { fields: TPricingSectionCardFields }) => ({
          cardTag: entry.fields.cardTag || null,
          packageName: entry.fields.packageName,
          packagePrice: entry.fields.packagePrice,
          oneReportPrice: entry.fields.oneReportPrice || null,
          disclaimer: entry.fields.disclaimer || null,
        }))
      : [];

    return {
      contentType: moduleContentType,
      module: {
        title: fields.title,
        description: fields.description,
        disclaimer: fields.disclaimer || null,
        disclaimerLinkText: fields.disclaimerLinkText || null,
        disclaimerLinkUrl: fields.disclaimerLinkUrl || null,
        pricingCards,
        scrollAnchor: PageModularApi.reduceScrollAnchor(fields.scrollAnchor),
      } satisfies ModulePricingSection,
    };
  }

  private static reduceNumberedSteps(
    fields: TModuleNumberedStepsFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const cards = Array.isArray(fields.cards)
      ? fields.cards.filter(isDefined).map((entry) => {
          const f = entry.fields;
          return {
            title: f.title || "",
            description: f.description || "",
          };
        })
      : [];

    return {
      contentType: moduleContentType,
      module: {
        badge: fields.badge || undefined,
        title: fields.title || "",
        cards,
      } satisfies ModuleNumberedSteps,
    };
  }

  private static reduceIconCards(
    fields: TModuleIconCardsFields,
    moduleContentType: ModuleContentType,
  ): PageModularModuleWrapper {
    const reduceCard = (entry: { fields: TIconCardFields }) => ({
      icon: entry.fields.icon ? reduceAssetToImage(entry.fields.icon).url : "",
      text: entry.fields.text || "",
    });

    const partnerLogo = fields.partnerLogo ? reduceAssetToImage(fields.partnerLogo) : null;

    return {
      contentType: moduleContentType,
      module: {
        partnerLogoUrl: partnerLogo ? partnerLogo.url : "",
        partnerLogoAlt: partnerLogo ? partnerLogo.title : undefined,
        title: fields.title || "",
        description: fields.description || "",
        section1: {
          title: fields.section1Title || "",
          description: fields.section1Description || "",
          cards: Array.isArray(fields.section1Cards) ? fields.section1Cards.filter(isDefined).map(reduceCard) : [],
        },
        section2: {
          title: fields.section2Title || "",
          description: fields.section2Description || "",
          cards: Array.isArray(fields.section2Cards) ? fields.section2Cards.filter(isDefined).map(reduceCard) : [],
        },
        scrollAnchor: PageModularApi.reduceScrollAnchor(fields.scrollAnchor),
      } satisfies ModuleIconCards,
    };
  }
}
